import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { differenceInMinutes } from 'date-fns';
import { RootReducer } from '@store/index';
import { auth } from '@api/Firebase/Firebase';
import { resetLoggedUser, setBranchOfficeSelected, setLoggedUser, setProfileSelected } from '@store/authStore';
import { showAlertError } from '@utils/Alerts/Error';
import { hideLoader } from '@utils/Loader';
import { refreshBranchOffices, refreshDistributionCenters, refreshSalesChannels } from '@utils/Catalogs';
import UserService from '@api/Users/UserCtrl';
import BranchOfficesService from '@api/BranchOffices/BranchOfficeCtrl';
import { INACTIVE_USER_MESSAGE, isUserActive, UserType } from '@api/Users/User';

/**
 * Bootstraps the user session: subscribes to Firebase auth changes, hydrates the
 * logged user, the selected branch office and the global Krispis catalogs into Redux.
 *
 * Returns `authChecked`, which is `false` until Firebase has resolved the initial
 * auth state. The router uses it to avoid rendering protected/public routes (and
 * flashing the wrong screen) before we actually know whether there is a session.
 */
export const useAuthBootstrap = () => {
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const { lastUpdate } = useSelector((state: RootReducer) => state.krispis);
    const [authChecked, setAuthChecked] = useState(false);
    const dispatch = useDispatch();

    const userService = useMemo(() => new UserService(), []);
    const branchOfficesService = useMemo(() => new BranchOfficesService(), []);

    const loadInitialData = async (user: UserType) => {
        try {
            const diff = differenceInMinutes(new Date(), new Date(lastUpdate));
            if (isNaN(diff) || !diff || diff > 60) {
                // Load all shared catalogs for every profile so the slice (the single
                // source of truth) is reliably populated regardless of who logs in.
                // refreshBranchOffices still scopes branch offices to the user.
                await Promise.all([refreshBranchOffices(user), refreshDistributionCenters(), refreshSalesChannels()]);
            }
        } catch (error: any) {
            showAlertError(error);
        }
    };

    const fetchInitialData = async (firebaseUser: any | null) => {
        try {
            if (!firebaseUser) {
                dispatch(resetLoggedUser());
                return;
            }
            const user = await userService.getUser();
            // Un usuario desactivado no entra, ni al hacer login ni al abrir la app con
            // una sesión guardada: los dos caminos pasan por aquí. `onLogout` vuelve a
            // disparar este listener con `null`, que limpia la sesión.
            if (!isUserActive(user)) {
                dispatch(resetLoggedUser());
                await userService.onLogout();
                showAlertError(INACTIVE_USER_MESSAGE);
                return;
            }
            // Set the session up front (and unconditionally) so the router can redirect
            // reliably. Doing this before catalog/branch-office loading means a slow or
            // failing fetch below can never skip it and leave us stuck on the login screen.
            dispatch(setLoggedUser(user));
            dispatch(setProfileSelected(user.profile.profile));
            //Load all initial data like sales channels, branch offices, etc.
            await loadInitialData(user);
            // Resolve the branch office for this user. Never trust a branch office persisted
            // by a previous session/user: keep it only if it actually belongs to the logged-in
            // user; otherwise derive theirs. Isolated in its own try/catch so a failure here
            // cannot undo the session set above.
            try {
                const allowedBranchOffices = user.profile.branchOffices || [];
                const canAccessAllBranchOffices = allowedBranchOffices.includes('all');
                let branchOffice = branchOfficeSelected;
                const persistedBranchIsAllowed =
                    !!branchOffice?.id && (canAccessAllBranchOffices || allowedBranchOffices.includes(branchOffice.id));
                if (!persistedBranchIsAllowed) {
                    branchOffice = await userService.getMainBranchOffice();
                }
                if (branchOffice && branchOffice.id) {
                    branchOffice = await branchOfficesService.getBranchOffice(branchOffice.id);
                }
                dispatch(setBranchOfficeSelected(branchOffice));
            } catch (branchError: any) {
                showAlertError(branchError);
            }
        } catch (error: any) {
            showAlertError(error);
        }
    };

    useEffect(() => {
        return auth.onAuthStateChanged(async (firebaseUser) => {
            await fetchInitialData(firebaseUser);
            setAuthChecked(true);
            // The session is now hydrated (profileSelected is set), so the router
            // redirects to the profile home. Turn off any loader started by the
            // login screen so it doesn't keep covering the screen.
            hideLoader();
        });
    }, []);

    return { authChecked };
};
